import { FunctionComponent, ReactElement, useCallback } from "react";

import { Button, Typography, styled } from "@mui/material";

import {
    DataGrid,
    GridColDef,
    GridRenderCellParams,
    GridRowParams,
    GridToolbar,
    GridValueGetterParams,
} from "@mui/x-data-grid";
import { format } from "date-fns";
import { Link, useNavigate } from "react-router-dom";

import { EmptyRecords } from "../../components/common";
import { useGetAdminGroups } from "../../hooks";
import { globalDateTimeFormat } from "../../utils/constants";

const Root = styled("main")`
    padding: ${({ theme }) => theme.spacing(3)};
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(3)};
`;

const TitleContainer = styled("div")`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const Title = styled(Typography)`
    font-size: 24px;
    font-weight: 500;
`;

const EmptyRecordsContainer = styled("div")`
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(1)};
    align-items: center;
`;

const GroupTitle = styled(Typography)`
    font-size: 16px;
    color: ${({ theme }) => theme.palette.text.primary};
`;

const Description = styled(Typography)`
    font-size: 13px;
    color: grey;
`;

const StyledCell = styled("div")`
    display: flex;
    flex-direction: column;
    align-items: start;
`;

const columns: GridColDef[] = [
    {
        field: "name",
        headerName: "Name",
        flex: 5,
        minWidth: 300,
        renderCell: (params: GridRenderCellParams) => (
            <StyledCell>
                <GroupTitle>{params.row.name}</GroupTitle>
                <Description>{params.row.description}</Description>
            </StyledCell>
        ),
    },

    {
        field: "createdAt",
        headerName: "Created on",
        flex: 1,
        valueGetter: (params: GridValueGetterParams) =>
            `${format(new Date(params.row.createdAt), globalDateTimeFormat)}`,
    },
    {
        field: "updatedAt",
        headerName: "Updated on",
        flex: 1,
        valueGetter: (params: GridValueGetterParams) =>
            `${format(new Date(params.row.updatedAt), globalDateTimeFormat)}`,
    },
];

export const ViewAdminGroupsScreen: FunctionComponent = (): ReactElement => {
    const adminGroupsQuery = useGetAdminGroups();

    const navigate = useNavigate();

    const handleRowClick = useCallback(
        (params: GridRowParams) => {
            navigate(`/admin-groups/${params.row.id}`);
        },
        [navigate],
    );

    return (
        <Root>
            <TitleContainer>
                <Title>Admin groups</Title>
                <Link to="/admin-groups/new">
                    <Button variant="contained">Create admin group</Button>
                </Link>
            </TitleContainer>

            {(adminGroupsQuery?.data?.records ?? []).length === 0 && (
                <EmptyRecords>
                    <EmptyRecordsContainer>
                        <Title>Manage admin groups</Title>
                        <Link to="/admin-groups/new">
                            <Button variant="contained">
                                Create admin group
                            </Button>
                        </Link>
                    </EmptyRecordsContainer>
                </EmptyRecords>
            )}

            {(adminGroupsQuery?.data?.records ?? []).length > 0 && (
                <DataGrid
                    rows={adminGroupsQuery?.data?.records ?? []}
                    columns={columns}
                    initialState={{
                        pagination: {
                            paginationModel: {
                                pageSize: 20,
                            },
                        },
                    }}
                    onRowClick={handleRowClick}
                    pageSizeOptions={[10, 20, 30]}
                    disableRowSelectionOnClick={true}
                    slots={{ toolbar: GridToolbar }}
                    slotProps={{
                        toolbar: {
                            showQuickFilter: true,
                        },
                    }}
                    disableColumnSelector={true}
                    disableDensitySelector={true}
                />
            )}
        </Root>
    );
};
